import Phaser from "phaser";

/**
 * Button press animation
 */
export function animateButtonPress(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.GameObject,
  onComplete?: () => void
): void {
  scene.tweens.add({
    targets: target,
    scaleX: 0.95,
    scaleY: 0.95,
    y: "+= 2",
    duration: 100,
    yoyo: true,
    ease: "Power2",
    onComplete: onComplete,
  });
}

/**
 * Pulse animation for success feedback
 */
export function animatePulse(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.GameObject,
  scale: number = 1.2,
  duration: number = 300
): void {
  scene.tweens.add({
    targets: target,
    scaleX: scale,
    scaleY: scale,
    duration: duration / 2,
    yoyo: true,
    ease: "Back.easeOut",
  });
}

/**
 * Shake animation for error feedback
 */
export function animateShake(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.GameObject,
  intensity: number = 8,
  duration: number = 400
): void {
  const targetWithPosition = target as Phaser.GameObjects.GameObject & { x: number };
  const startX = targetWithPosition.x;

  scene.tweens.add({
    targets: target,
    x: startX + intensity,
    duration: duration / 8,
    yoyo: true,
    repeat: 3,
    ease: "Power2",
    onComplete: () => {
      targetWithPosition.x = startX;
    },
  });
}

/**
 * Fade in animation
 */
export function animateFadeIn(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.GameObject,
  duration: number = 300,
  onComplete?: () => void
): void {
  const targetWithAlpha = target as Phaser.GameObjects.GameObject & { alpha: number };
  targetWithAlpha.alpha = 0;

  scene.tweens.add({
    targets: target,
    alpha: 1,
    duration: duration,
    ease: "Power2",
    onComplete: onComplete,
  });
}

/**
 * Fade out animation
 */
export function animateFadeOut(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.GameObject,
  duration: number = 300,
  onComplete?: () => void
): void {
  scene.tweens.add({
    targets: target,
    alpha: 0,
    duration: duration,
    ease: "Power2",
    onComplete: onComplete,
  });
}

/**
 * Scale in animation (pop in)
 */
export function animateScaleIn(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.GameObject,
  duration: number = 300,
  onComplete?: () => void
): void {
  const targetWithProps = target as Phaser.GameObjects.GameObject & {
    scaleX: number;
    scaleY: number;
    alpha: number;
  };

  targetWithProps.scaleX = 0;
  targetWithProps.scaleY = 0;
  targetWithProps.alpha = 0;

  scene.tweens.add({
    targets: target,
    scaleX: 1,
    scaleY: 1,
    alpha: 1,
    duration: duration,
    ease: "Back.easeOut",
    onComplete: onComplete,
  });
}

/**
 * Scale out animation (pop out)
 */
export function animateScaleOut(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.GameObject,
  duration: number = 200,
  onComplete?: () => void
): void {
  scene.tweens.add({
    targets: target,
    scaleX: 0,
    scaleY: 0,
    alpha: 0,
    duration: duration,
    ease: "Back.easeIn",
    onComplete: onComplete,
  });
}

/**
 * Slide in from direction
 */
export function animateSlideIn(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.GameObject,
  direction: "left" | "right" | "top" | "bottom",
  distance: number = 500,
  duration: number = 400,
  onComplete?: () => void
): void {
  const targetWithPosition = target as Phaser.GameObjects.GameObject & { x: number; y: number };
  const startX = targetWithPosition.x;
  const startY = targetWithPosition.y;

  switch (direction) {
    case "left":
      targetWithPosition.x = startX - distance;
      break;
    case "right":
      targetWithPosition.x = startX + distance;
      break;
    case "top":
      targetWithPosition.y = startY - distance;
      break;
    case "bottom":
      targetWithPosition.y = startY + distance;
      break;
  }

  scene.tweens.add({
    targets: target,
    x: startX,
    y: startY,
    duration: duration,
    ease: "Power2",
    onComplete: onComplete,
  });
}

/**
 * Counter increment animation
 */
export function animateCounterIncrement(
  scene: Phaser.Scene,
  textObject: Phaser.GameObjects.Text,
  fromValue: number,
  toValue: number,
  duration: number = 500,
  onComplete?: () => void
): void {
  const obj = { value: fromValue };

  scene.tweens.add({
    targets: obj,
    value: toValue,
    duration: duration,
    ease: "Power2",
    onUpdate: () => {
      textObject.setText(Math.floor(obj.value).toLocaleString());
    },
    onComplete: onComplete,
  });
}

/**
 * Star collect animation (arc to target)
 */
export function animateStarCollect(
  scene: Phaser.Scene,
  star: Phaser.GameObjects.GameObject,
  targetX: number,
  targetY: number,
  duration: number = 500,
  onComplete?: () => void
): void {
  const starWithPosition = star as Phaser.GameObjects.GameObject & { x: number; y: number };
  const startY = starWithPosition.y;

  // Calculate arc height
  const arcHeight = -100;

  scene.tweens.add({
    targets: star,
    x: targetX,
    duration: duration,
    ease: "Power2",
  });

  scene.tweens.add({
    targets: star,
    y: targetY,
    duration: duration,
    ease: "Sine.easeInOut",
    onUpdate: (tween) => {
      const t = tween.progress;
      const parabola = 4 * t * (1 - t);
      starWithPosition.y = startY + (targetY - startY) * t + arcHeight * parabola;
    },
    onComplete: onComplete,
  });
}

/**
 * Continuous rotation animation
 */
export function animateRotate(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.GameObject,
  duration: number = 2000,
  clockwise: boolean = true
): Phaser.Tweens.Tween {
  return scene.tweens.add({
    targets: target,
    angle: clockwise ? 360 : -360,
    duration: duration,
    repeat: -1,
    ease: "Linear",
  });
}
